'use strict';
/* global navigator */
import cookieHelper from './cookies';
import { isEmpty } from 'Helpers/objects';
import { buildURL } from './url';
import keyBy from 'lodash/keyBy';
import { isHomePage } from 'Helpers/olxAutos';
import { CONFIG } from 'Constants/config';

function matchLocation(locationsMap = {}, slug) {
    let indexPoint = -1;

    if (locationsMap.slugs) {
        indexPoint = locationsMap.slugs.indexOf(slug);
    }

    return indexPoint >= 0 ? locationsMap.points[indexPoint].split(',') : [];
}

function getBucketSize(categoriesTree, categoryID, configBucketSize) {
    let listingCategory;

    if (categoriesTree) {
        categoriesTree.forEach(category => {
            if (category.id === categoryID) {
                listingCategory = category;
            }
            else {
                category.sub_categories.forEach(subCategory => {
                    if (subCategory.id === categoryID) {
                        listingCategory = subCategory;
                    }
                });
            }
        });
    }

    return listingCategory && listingCategory.bucketSize || configBucketSize;
}

function getLocationName(addressComponents, level) {
    let lastComponentName = '';

    let isComplete = false;

    return addressComponents && addressComponents
        .sort(({ order: orderA = 0 }, { order: orderB = 0 }) => orderB - orderA)
        .reduce((acc, { name, type }, index) => {
            if ((index < level) && (lastComponentName !== name) && !isComplete) {
                acc.push(name);
            }
            if (type === 'STATE') {
                isComplete = true;
            }
            lastComponentName = name;
            return acc;
        }, []).join(', ');
}

const encodeCookieValue = value => encodeURIComponent(JSON.stringify(value));
const decodeCookieValue = value => JSON.parse(decodeURIComponent(value));

const createLocationPathCookie = (value = {}, name = 'locationPath') => {
    const defaultExpiration = 30;

    if (navigator && navigator.permissions) {
        const expiration = 1;

        return navigator.permissions.query({ name: 'geolocation' }).then(result => {
            if (result.state === 'denied') {
                return cookieHelper.createCookie(name, encodeCookieValue(value), defaultExpiration);
            }
            return cookieHelper.createCookie(name, encodeCookieValue(value), expiration);
        });
    }
    return cookieHelper.createCookie(name, encodeCookieValue(value), defaultExpiration);
};
const readLocationPathCookie = (name = 'locationPath') => {
    const findCookie = cookieHelper.readCookie(name);

    if (findCookie) {
        return decodeCookieValue(findCookie);
    }
    return undefined;
};
const removeLocationPathCookie = (name = 'locationPath') => {
    const findCookie = cookieHelper.readCookie(name);

    if (findCookie) {
        cookieHelper.eraseCookie(name);
    }
};
const getLocationPath = props => {
    const { geoID } = props.params;
    const locationFilter = (props.location.query && props.location.query.location)
        ? props.location.query.location.split?.(',')
        : undefined;

    if (locationFilter) {
        const [lat, lon] = locationFilter;

        return {
            lat: Number(lat),
            lon: Number(lon)
        };
    }
    else if (geoID) {
        return { id: geoID };
    }
    else if (props.selectedLocation) {
        return { id: props.selectedLocation.id };
    }
    return undefined;
};

const addressComponentsToString = (addressComponents = [], limit = 2, separator = ', ') => {
    if (!!addressComponents.length && addressComponents[0].type == 'COUNTRY') {
        addressComponents.reverse();
    }
    return addressComponents.slice(0, limit).reduce((acc, b) => {
        return acc ? acc.concat(`${separator}${b.name}`) : b.name;
    }, '');
};

const getNeighbourhoodAndCityName = locationTree => {
    const response = {
        neighbourhood: '',
        city: ''
    };

    if (!locationTree) {
        return response;
    }
    const locationTreeByKey = keyBy(locationTree, 'type');

    if (!isEmpty(locationTreeByKey.NEIGHBOURHOOD)) {
        response.neighbourhood = locationTreeByKey.NEIGHBOURHOOD.name;
    }

    if (!isEmpty(locationTreeByKey.CITY)) {
        response.city = locationTreeByKey.CITY.name;
    }

    return response;
};

const getNeighbourhoodAndCity = item => {
    const { metadata } = item;
    const locationsTree = metadata
        && metadata.locations
        && metadata.locations.length
        && metadata.locations[0].tree;

    return getNeighbourhoodAndCityName(locationsTree);
};

const locationsKeyMapper = (value, mapper) => {
    if (isEmpty(mapper)) {
        return false;
    }

    return mapper[value] || value;
};

const getLocationHeirarchy = (locations, category, config = { get: () => { } }) => {
    const heirarchy = [];

    if (locations) {
        const seoFriendlyBreadcrumb = config.get('seoFriendlyBreadcrumb');

        for (let i = locations.length - 2; i >= 0; i--) {
            const location = locations[i];
            const text = seoFriendlyBreadcrumb ? {
                text: location?.name
            } : {
                translation: {
                    id: 'categoryInLocation',
                    values: {
                        location: location?.name,
                        category: category?.name
                    }
                }
            };

            heirarchy.push({
                ...text,
                href: buildURL({ location, category }, {}, config.get('isOlxMobbi'))
            });
        }
    }
    return heirarchy;
};

const getCityLocationId = selectedLocation => {
    let locationId = selectedLocation.id;
    const addList = selectedLocation.addressComponents;

    if (addList) {
        const cityComponent = addList.filter(loc => loc.type === 'CITY');

        if (cityComponent.length > 0) {
            locationId = cityComponent[0].id;
        }
    }
    return locationId;
};

const getCategoryCover = location => location?.search?.match(/(\?|&)show-more=true(&|$)/);

const buildUrl = (params, config, useCategoryCoverIfNeeded = true) => {
    return buildURL(params, (useCategoryCoverIfNeeded ? { categoryCover: config.get('categoryCover') } : undefined), config.get('isOlxMobbi') === true);
};

const locationByHomeAndLatLong = (location, props, searchTerm, config, _setGPS, _setLocationGps) => {
    const {
        location: urlLocation,
        selectedCategory: category,
        isCurrentGPSLocation,
        updateLocation,
        resetLocation,
        router
    } = props;

    const urlKeywords = config.get('olxAutos', 'urlKeywords');
    const useCategoryCoverIfNeeded = getCategoryCover(urlLocation);
    const isDefaultCityEnabled = config.get(CONFIG.DEFAULT_CITY, CONFIG.ENABLED);

    // if the location change happened at root level of site(homepage)
    if (isHomePage({ location: urlLocation, urlKeywords })) {
        // if you have the lat long of the selected location
        if (location?.gps?.latitude || location?.gps?.longitude) {
            if (!isCurrentGPSLocation) {
                _setLocationGps(location);
                return _setGPS(location.gps);
            }
            return null;
        }
        // otherwise update the location in state
        return updateLocation(location);
    }

    // for other pages/url of site
    // if you have the lat long of selected location
    if (location?.gps?.latitude || location?.gps?.longitude) {
        // set the gps location, if its not the current location
        if (!isCurrentGPSLocation) {
            _setLocationGps(location);
            _setGPS(location.gps);
        }

        // we are gonna redirect to new page with new location selected
        router.push(buildUrl({ category, search: searchTerm }, config, useCategoryCoverIfNeeded));
    }
    else {
        // if gps location is current location
        if (isCurrentGPSLocation || isDefaultCityEnabled) {
            // update the selectedLocation in state
            updateLocation(location);
        }
        // redirect towards new url
        router.push(buildUrl({ location, category, search: searchTerm }, config, useCategoryCoverIfNeeded));
    }

    // if the selected location is of country type
    // update the state to go back to starting point/location
    if (!isDefaultCityEnabled && location.type === 'COUNTRY') {
        resetLocation();
    }

    return null;
};

const getLocationId = location => {
    if (!location) {
        return 'unknown';
    }

    if (location.type === 'CITY' || location.type === 'STATE' || location.type === 'COUNTRY') {
        return String(location.id);
    }

    const addressComponent = location.addressComponents && location.addressComponents.find(loc => loc.type === 'CITY');

    if (addressComponent) {
        return addressComponent.id;
    }

    return 'unknown';
};

export {
    matchLocation,
    getBucketSize,
    addressComponentsToString,
    createLocationPathCookie,
    readLocationPathCookie,
    getLocationPath,
    locationsKeyMapper,
    removeLocationPathCookie,
    getLocationHeirarchy,
    getLocationName,
    getNeighbourhoodAndCityName,
    getNeighbourhoodAndCity,
    getCityLocationId,
    getCategoryCover,
    locationByHomeAndLatLong,
    getLocationId
};
